import { useContext, type FC } from 'react';
import classNames from 'classnames';
import { useFetch } from 'loti-request';
import { useTranslation } from 'react-i18next';

import TeamName from 'app/components/TeamName';
import { formatDate } from 'app/utils/date';
import ConfigContext from 'app/common/ConfigContext';
import { getMatchGamesFetchUrl } from 'app/utils/entities/tournament';
import LoadingContainer from 'app/components/LoadingContainer';
import CircleLoader from 'app/components/CircleLoader/CircleLoader';
import { sort } from 'app/utils/array';
import { ERROR_UNKNOWN } from 'app/constants/errors';

import BracketGameRecapCs from './BracketGameRecapCs';
import BracketGameRecapMoba from './BracketGameRecapMoba';
import style from './style.module.scss';

interface Props {
  match: TournamentBracketMatch;
  videogame: VideogameSlug;
  videogameTitle: CounterStrikeVersion | null;
}

const BracketMatchRecap: FC<Props> = ({ match, videogame, videogameTitle }) => {
  const { t } = useTranslation();
  const configContext = useContext(ConfigContext);
  const { lang } = configContext;

  const { opponents } = match;
  const opponent1 = opponents.at(0);
  const opponent2 = opponents.at(1);

  const {
    status,
    data: gamesData,
    error
  } = useFetch<BracketGame[]>({
    url: getMatchGamesFetchUrl(match.id, configContext)
  });

  return (
    <div className={style.bracketMatchRecap}>
      <TeamName.Container className={style.scoresContainer}>
        <TeamName
          team={opponent1}
          teamNamesClassName={classNames(style.teamName, {
            [style.winner]: opponent1?.id === match.winner_id,
            [style.loser]: opponent2?.id === match.winner_id
          })}
          orientation="reversed"
        />
        <span
          className={classNames(style.teamScore, {
            [style.winner]: opponent1?.id === match.winner_id,
            [style.loser]: opponent2?.id === match.winner_id
          })}
        >
          {opponent1?.score}
        </span>
        <span className={style.vsText}>vs</span>
        <span
          className={classNames(style.teamScore, {
            [style.winner]: opponent2?.id === match.winner_id,
            [style.loser]: opponent1?.id === match.winner_id
          })}
        >
          {opponent2?.score}
        </span>
        <TeamName
          team={opponent2}
          teamNamesClassName={classNames(style.teamName, {
            [style.winner]: opponent2?.id === match.winner_id,
            [style.loser]: opponent1?.id === match.winner_id
          })}
        />
      </TeamName.Container>
      <LoadingContainer
        data={gamesData}
        status={status}
        error={error ? ERROR_UNKNOWN : null}
        loader={<CircleLoader size={20} />}
      >
        <div className={style.gamesRecapContainer}>
          {gamesData &&
            sort(gamesData, game => game.position).map(game => {
              switch (videogame) {
                case 'cs-go': {
                  return (
                    <BracketGameRecapCs
                      key={game.id}
                      game={game as BracketGameCs}
                      opponent1={opponent1}
                      opponent2={opponent2}
                      variation={videogameTitle ?? 'cs-go'}
                    />
                  );
                }
                case 'dota-2': {
                  return (
                    <BracketGameRecapMoba
                      key={game.id}
                      game={game as BracketGameDota}
                      opponent1={opponent1}
                      opponent2={opponent2}
                      videogameSlug="dota-2"
                      oddStyle={game.position % 2 === 0}
                    />
                  );
                }
                case 'league-of-legends': {
                  return (
                    <BracketGameRecapMoba
                      key={game.id}
                      game={game as BracketGameLol}
                      opponent1={opponent1}
                      opponent2={opponent2}
                      videogameSlug="league-of-legends"
                      oddStyle={game.position % 2 === 0}
                    />
                  );
                }
                default:
                  return null;
              }
            })}
        </div>
      </LoadingContainer>
      <div className={style.matchInfo}>
        <span>
          {match.begin_at &&
            formatDate(new Date(match.begin_at), lang, {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
        </span>
        <span>
          {t(`component_bracket-match-recap_match-type_${match.match_type}`, {
            games: match.number_of_games
          })}
        </span>
      </div>
    </div>
  );
};

BracketMatchRecap.displayName = 'BracketMatchRecap';

export default BracketMatchRecap;
